/**
 * One-click unsubscribe. Public on purpose — a member who wants out of club email
 * must not have to remember a password to get out. Anything else earns you a spam
 * complaint instead of an unsubscribe.
 */
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface UnsubInfo {
  firstName: string;
  email: string | null;
  optedOut: boolean;
}

export function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['unsubscribe', token],
    queryFn: () => api.get<{ data: UnsubInfo }>(`/unsubscribe?token=${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

  const mut = useMutation({
    mutationFn: (resubscribe: boolean) =>
      api.post<{ data: { optedOut: boolean } }>('/unsubscribe', { token, resubscribe }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['unsubscribe', token] }),
  });

  if (!token) {
    return (
      <Shell>
        <p className="text-sm text-cream-300/70">This link is missing its token.</p>
      </Shell>
    );
  }

  if (query.isLoading) {
    return (
      <Shell>
        <p className="text-sm text-cream-300/60">Loading…</p>
      </Shell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Shell>
        <p className="text-sm text-cream-300/70">
          This unsubscribe link is no longer valid. If you'd like to change your email settings,
          you can do it from your account.
        </p>
      </Shell>
    );
  }

  const { firstName, email, optedOut } = query.data;

  return (
    <Shell>
      {optedOut ? (
        <>
          <p className="text-sm leading-relaxed text-cream-200">
            You're unsubscribed{email ? <> — we won't email <strong>{email}</strong> again</> : null}.
          </p>
          <p className="mt-2 text-sm text-cream-300/60">
            You'll still see club news in your inbox when you sign in, and important account mail
            (receipts, password resets) will still reach you.
          </p>
          <button
            type="button"
            className="btn-ghost mt-6"
            disabled={mut.isPending}
            onClick={() => mut.mutate(true)}
          >
            {mut.isPending ? 'Working…' : 'Actually, resubscribe me'}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm leading-relaxed text-cream-200">
            {firstName}, this will stop club email to{' '}
            {email ? <strong>{email}</strong> : 'your address'}.
          </p>
          <p className="mt-2 text-sm text-cream-300/60">
            You'll still see everything in your inbox when you sign in, and we'll still send
            receipts and password resets.
          </p>
          <button
            type="button"
            className="btn-primary mt-6"
            disabled={mut.isPending}
            onClick={() => mut.mutate(false)}
          >
            {mut.isPending ? 'Working…' : 'Unsubscribe me'}
          </button>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md py-12">
      <div className="card p-8">
        <h1 className="font-display text-2xl text-cream-50">Email preferences</h1>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
